import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { UserProfileFacade } from '@features/user/facades/user-profile.facade';
import type { Language } from '@kerberos-compliance/compliance-shared-ts/common/i18n/enums/language.enums';
import {
  LanguageSelectComponent,
  NotificationMessageType,
  NotificationService,
  TranslationService,
} from '@kerberos-compliance/compliance-shared-ui';

@Component({
  selector: 'aad-language',
  templateUrl: 'language.component.html',
  imports: [LanguageSelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageComponent {
  private readonly translateService = inject(TranslationService);

  private readonly userProfileFacade = inject(UserProfileFacade);

  private readonly notificationService = inject(NotificationService);

  public isCollapsed = input.required<boolean>();

  public currentLanguage = computed(() => this.translateService.activeLanguage());

  public languageOptions = computed(() => this.translateService.languageOptions());

  /**
   * Changes the application's current language to the specified language.
   * It would also save the selected language in the user's profile.
   * But if the same language is selected, it would just ignore it.
   *
   * @param {Language} language - The new language to set as the active language.
   * @returns {Promise<void>} A promise that resolves when the language change has been processed.
   */
  public async changeLanguage(language: Language): Promise<void> {
    if (language === this.translateService.activeLanguage()) {
      return;
    }
    await this.saveUserProfile(language);
  }

  /**
   * Updates the user's profile with the selected and value.
   *
   * @param {Language} language - The new value to be set for the specified field.
   * @returns {Promise<void>} A promise that resolves once the user profile has been successfully updated.
   */
  private async saveUserProfile(language: Language): Promise<void> {
    const updatedProfile = await this.userProfileFacade.updateUserProfile({ language });
    if (updatedProfile) {
      queueMicrotask(() => {
        this.notificationService.success(NotificationMessageType.UserProfileUpdated);
      });
    }
  }
}
